<div class="min-h-screen p-4 md:p-6 max-w-5xl mx-auto">

  <!-- Header / Tabs + Search -->
  <div class="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
    <div class="flex bg-[#12141f] p-1 rounded-xl border border-white/5">
      <button
        (click)="activeTab.set(PoolTab.LIVE)"
        [class]="activeTab() === PoolTab.LIVE ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'"
        class="px-7 py-2 rounded-lg font-bold text-sm uppercase tracking-wide transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md active:translate-y-0">
        Live
      </button>
      <button
        (click)="activeTab.set(PoolTab.COMPLETED)"
        [class]="activeTab() === PoolTab.COMPLETED ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'"
        class="px-7 py-2 rounded-lg font-bold text-sm uppercase tracking-wide transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md active:translate-y-0">
        Completed
      </button>
      <button (click)="activeTab.set(PoolTab.STAKED)"
        [class]="activeTab() === PoolTab.STAKED ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'"
        class="px-7 py-2 rounded-lg font-bold text-sm uppercase tracking-wide transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md active:translate-y-0">
        My Stakes
      </button>
    </div>

    <div class="relative w-full md:w-72">
      <input
        type="text"
        [ngModel]="searchQuery()"
        (ngModelChange)="searchQuery.set($event)"
        placeholder="Search Pool"
        class="w-full bg-[#12141f] border border-white/10 rounded-xl py-2.5 px-10 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-purple-500/50 transition-all">
      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </span>
    </div>
  </div>

  <!-- Loading State -->
  @if (isLoading()) {
    <div class="flex flex-col items-center justify-center py-24">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mb-4"></div>
      <p class="text-gray-500 text-sm">Loading pools...</p>
    </div>
  } @else {

    <!-- Pool List Container -->
    <div class="bg-[#12141f] border border-white/5 rounded-2xl overflow-hidden">

      @for (pool of getFilteredPools(); track pool.id; let last = $last) {

        <!-- Pool Row -->
        <div
          (click)="togglePoolExpand(pool.id)"
          class="cursor-pointer hover:bg-white/[0.02] transition-colors"
          [class.border-b]="!last || pool.isExpanded"
          [class.border-white/5]="!last || pool.isExpanded">

          <div class="grid grid-cols-[1.6fr_1fr_1fr_1fr_1fr_0.7fr_0.7fr_auto] gap-2 items-center px-6 py-5">

            <!-- Pool Name -->
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-yellow-400 font-bold text-[15px]">
                {{getMonths(pool.maturityPeriod)}} Months Pool
              </span>
              @if (isStakingEnded(pool)) {
                <span class="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30">
                  Stake Time Ended
                </span>
              }
            </div>

            <!-- My Return -->
            <div>
              <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-0.5">My Return</span>
              <span class="text-white text-sm font-medium">
                {{toNumber(pool.totalUserRewards) > 0 ? pool.totalUserRewards : '–'}}
              </span>
            </div>

            <!-- My Deposit Amount -->
            <div>
              <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-0.5">My Deposit Amount</span>
              <span
                class="text-sm font-medium"
                [class]="toNumber(pool.totalUserStaked) > 0 ? 'text-white font-bold' : 'text-gray-400'">
                {{toNumber(pool.totalUserStaked) > 0 ? pool.totalUserStaked : '–'}}
              </span>
            </div>

            <!-- Expiry Date -->
            <div>
              <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-0.5">Expiry Date</span>
              <span class="text-gray-400 text-sm">
                {{pool.userPoolExpiry > 0 ? (pool.userPoolExpiry * 1000 | date:'MMM d, yyyy') : '–'}}
              </span>
            </div>

            <!-- Participants -->
            <div>
              <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-0.5">Participants</span>
              <span class="text-white text-sm font-bold">{{pool.participantCount}}</span>
            </div>

            <!-- APR -->
            <div>
              <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-0.5">APR</span>
              <span class="text-white text-sm font-bold">{{pool.apy}}%</span>
            </div>

            <!-- Lockup Period -->
            <div>
              <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-0.5">Lockup Period</span>
              <span class="text-white text-sm font-bold">{{getMonths(pool.maturityPeriod)}} Months</span>
            </div>

            <!-- Chevron -->
            <div class="flex items-center justify-end">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-gray-500 transition-transform duration-300"
                [class.rotate-180]="pool.isExpanded"
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

          </div>
        </div>

        <!-- ═══ Expanded Detail Panel ═══ -->
        @if (pool.isExpanded) {
          <div [@expandCollapse] class="overflow-hidden">
            <div
              class="border-t border-white/5 px-6 py-5"
              [class.border-b]="!last"
              [class.border-white/5]="!last">

            <div class="bg-[#1a1d2e] border border-white/5 rounded-xl p-5">

              <!-- Stats Row -->
              <div class="grid grid-cols-2 md:grid-cols-6 gap-5 mb-5">
                <div>
                  <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-1">Maximum Pool staking Amount</span>
                  <span class="text-white text-sm font-bold">
                    {{toNumber(pool.maxStakingAmount) > 0 ? (pool.maxStakingAmount | number:'1.0-2') + ' LNY' : 'No Limit'}}
                  </span>
                </div>
                <div>
                  <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-1">Amount Staked in pool</span>
                  <span class="text-white text-sm font-bold">{{pool.totalStaked}} LNY</span>
                </div>
                <div>
                  <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-1">Pool Expiry time</span>
                  <span class="text-white text-sm font-bold">
                    {{pool.stakingEndTime > 0 ? (pool.stakingEndTime * 1000 | date:'MMM d, yyyy | HH:mm') : '–'}}
                  </span>
                </div>
                <div>
                  <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-1">My Staked Amount</span>
                  <span class="text-white text-sm font-bold">
                    {{toNumber(pool.totalUserStaked) > 0 ? (pool.totalUserStaked | number:'1.0-2') + ' LNY' : '0 LNY'}}
                  </span>
                </div>
                <div>
                  <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-1">My Rewards</span>
                  <span class="text-white text-sm font-bold">
                    {{toNumber(pool.totalUserRewards) > 0 ? (pool.totalUserRewards | number:'1.0-2') + ' LNY' : '0 LNY'}}
                  </span>
                </div>
                <div>
                  <span class="text-gray-500 text-[10px] uppercase tracking-wider block mb-1">Status</span>
                  <div class="flex items-center gap-1.5">
                    <div class="w-2 h-2 rounded-full" [class]="(pool.isActive && !isStakingEnded(pool)) ? 'bg-purple-500' : 'bg-red-500'"></div>
                    <span class="text-white text-sm font-bold">{{(pool.isActive && !isStakingEnded(pool)) ? 'Active' : 'Ended'}}</span>
                  </div>
                </div>
              </div>

              <!-- Stake Button -->
              @if (pool.isActive) {
                <div class="mb-5">
                  <button
                    (click)="$event.stopPropagation(); openStakeModal(pool.id)"
                    [disabled]="isStakingEnded(pool)"
                    [class]="isStakingEnded(pool)
                      ? 'px-5 py-2 bg-gray-700 text-gray-400 cursor-not-allowed font-bold text-sm rounded-lg opacity-60 transition-all duration-200'
                      : 'px-5 py-2 bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-sm rounded-lg transition-all duration-200 shadow-lg shadow-yellow-400/10 hover:-translate-y-0.5 hover:shadow-yellow-400/20 active:translate-y-0'">
                    {{isStakingEnded(pool) ? 'Staking Ended' : 'Stake'}}
                  </button>
                </div>
              }

              <!-- ═══ Individual Stakes Table ═══ -->
              @if (pool.userStakes.length > 0) {
                <div class="border-t border-white/5 pt-5">
                  <p class="text-gray-500 text-xs uppercase tracking-widest font-bold mb-3">
                    Your Stakes ({{pool.userStakes.length}})
                  </p>

                  <!-- Column Headers -->
                  <div class="grid grid-cols-[2fr_1.5fr_1.5fr_2.5fr] gap-3 px-3 mb-2">
                    <span class="text-gray-600 text-[10px] uppercase tracking-wider">Amount</span>
                    <span class="text-gray-600 text-[10px] uppercase tracking-wider">Rewards</span>
                    <span class="text-gray-600 text-[10px] uppercase tracking-wider">Remaining</span>
                    <span class="text-gray-600 text-[10px] uppercase tracking-wider">Actions</span>
                  </div>

                  <!-- Stake Rows -->
                  <div class="flex flex-col gap-1" [@stakeList]>
                    @for (stake of pool.userStakes; track stake.index) {
                      <div class="stake-row grid grid-cols-[2fr_1.5fr_1.5fr_2.5fr] gap-3 items-center bg-black/20 hover:bg-black/30 rounded-xl px-3 py-3 border border-white/5 transition-colors">

                        <!-- Amount -->
                        <span class="text-white text-sm font-bold">{{stake.amount | number:'1.0-2'}} LNY</span>

                        <!-- Rewards -->
                        <span class="text-purple-400 text-sm font-bold">{{stake.pendingRewards | number:'1.0-2'}} LNY</span>

                        <!-- Countdown + progress bar -->
                        <div>
                          <span
                            class="font-mono font-bold text-xs block mb-1"
                            [class]="stake.isMatured ? 'text-green-400' : 'text-yellow-400'">
                            {{stake.formattedCountdown}}
                          </span>
                          <div class="h-1 w-full bg-black/40 rounded-full overflow-hidden">
                            <div
                              class="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full transition-all duration-1000"
                              [style.width.%]="stake.isMatured ? 100 : stake.progressPercent">
                            </div>
                          </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex gap-2 flex-wrap">
                          <button
                            (click)="$event.stopPropagation(); handleClaim(pool.id, stake.index)"
                            [disabled]="toNumber(stake.pendingRewards) <= 0 || isPending()"
                            class="px-3 py-2.5 bg-purple-600/80 hover:bg-purple-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs rounded-lg transition-all duration-200 whitespace-nowrap transform hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:transform-none disabled:shadow-none relative overflow-hidden bg-gradient-to-b from-white/20 via-transparent to-black/20">
                            Claim ({{stake.pendingRewards | number:'1.0-2'}} LNY)
                          </button>
                          <button
                            (click)="$event.stopPropagation(); openWithdrawModal(pool.id, stake)"
                            [disabled]="isPending()"
                            class="px-3 py-2.5 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold text-xs rounded-lg transition-all duration-200 whitespace-nowrap transform hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:transform-none disabled:shadow-none relative overflow-hidden bg-gradient-to-b from-white/20 via-transparent to-black/20">
                            Unstake ({{getTotalWithdrawAmount(stake) | number:'1.0-2'}} LNY)
                          </button>
                        </div>

                      </div>
                    }
                  </div>
                </div>
              } @else {
                <div class="border-t border-white/5 pt-5 text-center py-6">
                  <p class="text-gray-600 text-sm">No active stakes in this pool</p>
                </div>
              }

            </div>
          </div>
          </div>
        }

      }

      @if (getFilteredPools().length === 0) {
        <div class="text-center py-20">
          <p class="text-gray-600 text-sm">No pools found</p>
        </div>
      }

    </div>
  }

</div>

<!-- ═══════════════════ STAKE MODAL ═══════════════════ -->
@if (showStakeModal()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div (click)="showStakeModal.set(false)" class="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

    <div class="relative w-full max-w-sm bg-[#C4B5FD] rounded-3xl p-7 shadow-2xl">
      <div class="text-center mb-5">
        <h2 class="text-4xl font-groozila text-white uppercase tracking-wider drop-shadow-lg">Stake Loony</h2>
        <div class="h-1 w-20 bg-yellow-400 mx-auto mt-2 rounded-full"></div>
      </div>

      <div class="flex justify-between items-center mb-3 px-1">
        <span class="text-purple-900/60 font-bold uppercase text-xs tracking-widest">Available Balance</span>
        <span class="text-purple-900 font-black text-sm">{{userBalance() | number:'1.0-2'}} LNY</span>
      </div>

      <div class="relative mb-5">
        <input
          type="number"
          [ngModel]="stakeInputAmount()"
          (ngModelChange)="stakeInputAmount.set($event)"
          placeholder="0.00"
          class="w-full bg-white rounded-2xl py-5 px-5 text-xl font-groozila text-purple-900 placeholder:text-purple-200 focus:outline-none shadow-inner">
        <button
          (click)="setMaxStake()"
          class="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-xs rounded-full shadow-md transition-all duration-200 transform hover:scale-105 hover:shadow-lg active:scale-100">
          MAX
        </button>
      </div>

      <button
        (click)="handleStake()"
        [disabled]="isPending() || !stakeInputAmount() || toNumber(stakeInputAmount()) <= 0 || (modalPoolId() !== null && isStakingEnded(getPoolById(modalPoolId()!)))"
        class="w-full py-4 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50 text-black font-groozila text-2xl rounded-2xl shadow-[0_8px_0_#ca8a04] transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-[0_10px_0_#ca8a04] active:translate-y-1 active:shadow-[0_3px_0_#ca8a04] mb-3 disabled:transform-none">
        {{isPending() ? 'STAKING...' : 'STAKE LNY'}}
      </button>

      <button
        (click)="showStakeModal.set(false)"
        class="w-full text-purple-900/40 hover:text-purple-900 font-bold text-xs uppercase tracking-widest transition-colors duration-200">
        Cancel
      </button>
    </div>
  </div>
}

<!-- ═══════════════════ WITHDRAW MODAL ═══════════════════ -->
@if (showWithdrawModal() && selectedStakeForWithdraw(); as stake) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div (click)="showWithdrawModal.set(false)" class="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

    <div class="relative w-full max-w-sm bg-[#C4B5FD] rounded-3xl p-7 shadow-2xl border-4 border-white/20">
      <div class="text-center mb-5">
        <h2 class="text-4xl font-groozila text-white uppercase tracking-wider drop-shadow-lg">Withdraw Loony</h2>
        <div class="h-1 w-20 bg-yellow-400 mx-auto mt-2 rounded-full"></div>
      </div>

      @if (!stake.isMatured && stake.penaltyPercent > 0) {
        <div class="bg-white/20 rounded-xl p-3 mb-4 flex items-start gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-purple-900 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <p class="text-purple-900 text-xs font-medium leading-relaxed">
            Early withdrawal (<strong>{{stake.formattedCountdown}}</strong> remaining) incurs a
            <strong>{{stake.penaltyPercent}}% penalty</strong>. You will receive approximately
            <strong>{{getTotalWithdrawAmount(stake) | number:'1.0-2'}} LNY</strong>.
          </p>
        </div>
      }

      <div class="bg-white rounded-2xl p-5 mb-5 shadow-inner">
        <span class="text-gray-400 text-[10px] uppercase tracking-widest block mb-1">Amount to be withdrawn</span>
        <span class="text-3xl font-groozila text-purple-900">{{getTotalWithdrawAmount(stake) | number:'1.0-2'}} LNY</span>
        @if (!stake.isMatured && stake.penaltyPercent > 0) {
          <p class="text-red-400 text-xs mt-2 font-medium">
            After {{stake.penaltyPercent}}% penalty → you receive <strong>{{getTotalWithdrawAmount(stake) | number:'1.0-2'}} LNY</strong>
          </p>
        }
      </div>

      <button
        (click)="handleWithdraw()"
        [disabled]="isPending()"
        class="w-full py-4 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50 text-black font-groozila text-2xl rounded-2xl shadow-[0_8px_0_#ca8a04] transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-[0_10px_0_#ca8a04] active:translate-y-1 active:shadow-[0_3px_0_#ca8a04] mb-3 disabled:transform-none">
        {{isPending() ? 'WITHDRAWING...' : 'Withdraw Loony'}}
      </button>

      <button
        (click)="showWithdrawModal.set(false)"
        class="w-full text-purple-900/40 hover:text-purple-900 font-bold text-xs uppercase tracking-widest transition-colors duration-200">
        Maybe Later
      </button>
    </div>
  </div>
}
